import { useRef, useId } from 'react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { LoadingSkeleton, ErrorState } from '@repro/ui';

export interface OrgDetailData {
  id: string;
  name: string;
  email: string;
  subscription?: {
    planName: string;
    planPrice: string;
    renewsAt: string;
    paymentMethod: string;
  };
  usage?: {
    users: { current: number; max: number | 'unlimited' };
    projects: { current: number; max: number | 'unlimited' };
    storageGb: number;
  };
  invoices?: {
    id: string;
    statusLabel: string;
    statusVariant: 'success' | 'warning' | 'error' | 'ghost';
    dateLabel: string;
    amountLabel: string;
    hostedUrl?: string;
  }[];
}

interface OrgDetailPanelProps {
  open: boolean;
  onClose: () => void;
  org?: OrgDetailData;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onManageSubscription?: () => void;
  onCancelSubscription?: () => void;
}

const badgeClass: Record<
  NonNullable<OrgDetailData['invoices']>[number]['statusVariant'],
  string
> = {
  success: 'badge badge-success',
  warning: 'badge badge-warning',
  error: 'badge badge-error',
  ghost: 'badge badge-ghost',
};

/**
 * Slide-over panel rendering details for one organisation. Replaces the
 * inline expansion in v1's DashboardFixed and the v1 /partner/dashboard
 * organisation drilldown.
 *
 * Q6 (migration map): panel, not a route. The Dashboard page drives the
 * `open` state from a `?org=<id>` URL search param so the panel is
 * shareable + bookmarkable + back-button-closable (Q2 deep-link).
 */
export default function OrgDetailPanel({
  open,
  onClose,
  org,
  loading = false,
  error = null,
  onRetry,
  onManageSubscription,
  onCancelSubscription,
}: OrgDetailPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, onClose);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default bg-base-content/30"
        onClick={onClose}
        aria-label="Close panel"
        tabIndex={-1}
      />
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col gap-6 overflow-y-auto border-l border-base-300 bg-base-100 p-8 shadow-xl"
      >
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-0.5">
            <p id={titleId} className="text-xl font-bold text-base-content">
              {org?.name ?? 'Organisation'}
            </p>
            {org?.email && (
              <p className="text-sm text-base-content/70">{org.email}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-lg text-base-content/70 hover:text-base-content"
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        {loading && <LoadingSkeleton rows={6} />}

        {error && !loading && <ErrorState body={error} onRetry={onRetry} />}

        {!loading && !error && org?.subscription && (
          <section
            className="flex flex-col gap-3"
            aria-labelledby={`${titleId}-sub`}
          >
            <h3
              id={`${titleId}-sub`}
              className="text-sm font-semibold text-base-content"
            >
              Subscription
            </h3>
            <dl className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-base-content/70">Plan</dt>
                <dd className="font-semibold text-base-content">
                  {org.subscription.planName} · {org.subscription.planPrice}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-base-content/70">Renews</dt>
                <dd className="font-semibold text-base-content">
                  {org.subscription.renewsAt}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-base-content/70">Method</dt>
                <dd className="font-semibold text-base-content">
                  {org.subscription.paymentMethod}
                </dd>
              </div>
            </dl>
          </section>
        )}

        {!loading && !error && org?.usage && (
          <section
            className="flex flex-col gap-3"
            aria-labelledby={`${titleId}-usage`}
          >
            <h3
              id={`${titleId}-usage`}
              className="text-sm font-semibold text-base-content"
            >
              Usage this period
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <UsageTile
                label="Users"
                value={`${org.usage.users.current} / ${org.usage.users.max}`}
              />
              <UsageTile
                label="Projects"
                value={`${org.usage.projects.current} / ${org.usage.projects.max}`}
              />
              <UsageTile label="Storage" value={`${org.usage.storageGb} GB`} />
            </div>
          </section>
        )}

        {!loading && !error && org?.invoices && org.invoices.length > 0 && (
          <section
            className="flex flex-col gap-2"
            aria-labelledby={`${titleId}-inv`}
          >
            <h3
              id={`${titleId}-inv`}
              className="text-sm font-semibold text-base-content"
            >
              Recent invoices
            </h3>
            <ul className="flex flex-col">
              {org.invoices.map((inv) => {
                const inner = (
                  <>
                    <span className={badgeClass[inv.statusVariant]}>
                      {inv.statusLabel}
                    </span>
                    <span className="text-sm text-base-content">
                      {inv.dateLabel}
                    </span>
                    <span className="ml-auto text-sm font-semibold text-base-content">
                      {inv.amountLabel}
                    </span>
                  </>
                );
                return (
                  <li
                    key={inv.id}
                    className="flex items-center gap-3 border-t border-base-300 py-2 first:border-t-0"
                  >
                    {inv.hostedUrl ? (
                      <a
                        href={inv.hostedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center gap-3 hover:underline"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div className="flex flex-1 items-center gap-3">
                        {inner}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {!loading &&
          !error &&
          (onManageSubscription || onCancelSubscription) && (
            <div className="mt-auto flex flex-col gap-2 pt-4">
              {onManageSubscription && (
                <button
                  type="button"
                  onClick={onManageSubscription}
                  className="btn btn-primary w-full"
                >
                  Manage subscription
                </button>
              )}
              {onCancelSubscription && (
                <button
                  type="button"
                  onClick={onCancelSubscription}
                  className="btn btn-ghost w-full"
                >
                  Cancel subscription
                </button>
              )}
            </div>
          )}
      </aside>
    </>
  );
}

function UsageTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-box bg-base-200 p-3">
      <p className="text-xs text-base-content/70">{label}</p>
      <p className="text-lg font-bold text-base-content">{value}</p>
    </div>
  );
}
